import * as React from 'react';
import { useState } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { useStyles } from './styles';
import { EXTRA_FEATURES_CLIENT_IDS } from '../../constants';
import OrderVoidLabel from '../OrderVoidLabel';
import OrderItemCancelForm from '../OrderItemCancelForm';
import { useCan } from '../../auth/useCan';
import { ORDERS_RW } from '../../auth/requirements';
import { KeyValueList, Panel, StatusChip } from '../ui';
import { itemStatus, orderStatus } from '../../lib/orderStatus';
import { formatAmount, formatDate } from '../../lib/formatDate';

/** API-provided URLs are rendered as links only when they are plain http(s). */
const safeHref = (url: unknown): string | undefined =>
    typeof url === 'string' && /^https?:\/\//i.test(url) ? url : undefined;

/**
 * Body of the order details page: items, shipments and history in the main column; summary,
 * recipient and totals in the side column (first on phones). Prices, the recipient and every
 * action are shown to `order-integration-rw` only; the backend enforces the real access.
 */
export default function GeneralForm(props: any) {
    const { data } = props.data;
    const classes = useStyles();
    const [isLabelPopUp, setLabelPopUp] = useState(false);
    const [labelInfo, setLabelInfo] = useState<any>();
    const [isItemActionPopUp, setItemActionPopUp] = useState(false);
    const [selectedOrderItemId, setSelectedOrderItemId] = useState<number | string | null>(null);
    const [itemActionType, setItemActionType] = useState<'cancel' | 'decline' | null>(null);
    const canManageOrders = useCan(ORDERS_RW);
    const extraFeatures = EXTRA_FEATURES_CLIENT_IDS.includes(String(data.clientId));
    const status = orderStatus(data.orderStatusId);

    const openItemAction = (itemId: number | string, action: 'cancel' | 'decline') => {
        setSelectedOrderItemId(itemId);
        setItemActionType(action);
        setItemActionPopUp(true);
    };
    const voidLabelPopUpHandler = (info) => {
        setLabelInfo(info);
        setLabelPopUp(true);
    };

    const renderPrintFile = (file, index: number) => {
        if (!file) return null;
        const ready = safeHref(file.printFileReadyUrl);
        const original = safeHref(file.url);
        if (ready) {
            return (
                <Tooltip
                    key={index}
                    title={<img className={classes.preview} src={ready} alt="" />}
                    enterTouchDelay={200}
                    slotProps={{ tooltip: { sx: { maxWidth: 'none', p: 0.5, bgcolor: 'transparent' } } }}
                >
                    <a href={ready} target="_blank" rel="noreferrer" aria-label={`Print file ${file.type || index + 1}, opens in a new tab`}>
                        <img className={classes.thumb} src={ready} alt="" loading="lazy" />
                    </a>
                </Tooltip>
            );
        }
        if (original) {
            return (
                <a key={index} className={classes.thumbLink} href={original} target="_blank" rel="noreferrer">
                    {file.type ? `${file.type} (original)` : 'Original file'}
                </a>
            );
        }
        return <span key={index} className={classes.thumbLink}>{file.type || '—'}</span>;
    };

    const recipient = data.recipient;

    return (
        <>
            <OrderVoidLabel isPopUp={isLabelPopUp} popUpHandler={() => setLabelPopUp(false)} labelInfo={labelInfo} />
            <OrderItemCancelForm
                isPopUp={isItemActionPopUp}
                popUpHandler={setItemActionPopUp}
                orderItemId={selectedOrderItemId}
                actionType={itemActionType}
            />
            <div className={classes.grid}>
                <div className={classes.main}>
                    <Panel
                        title="Items"
                        badges={<StatusChip size="sm" tone="neutral">{data.items?.length ?? 0}</StatusChip>}
                        padding="none"
                    >
                        {data.items && data.items.length > 0 ? (
                            <div className={classes.scroll}>
                                <Table aria-label="Order items">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Print files</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            {extraFeatures && <TableCell>Status</TableCell>}
                                            {canManageOrders && <TableCell align="right">Price</TableCell>}
                                            {canManageOrders && <TableCell align="right">Total</TableCell>}
                                            {extraFeatures && canManageOrders && <TableCell align="right">Actions</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.items.map((row, index) => {
                                            const item = itemStatus(row.itemStatusId, row.itemStatusCode);
                                            const struck = row.requireCancelPayment === 1 && (row.itemStatusId === 3 || row.itemStatusId === 4);
                                            return (
                                                <TableRow key={row.orderItemId ?? index}>
                                                    <TableCell>
                                                        <div className={classes.thumbs}>
                                                            {(row.printFiles || []).map(renderPrintFile)}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className={classes.mono}>{row.sku || '—'}</TableCell>
                                                    <TableCell>
                                                        <div className={classes.mono}>{row.orderItemId ?? '—'}</div>
                                                        <div className={classes.secondary}>External {row.externalItemId || '—'}</div>
                                                    </TableCell>
                                                    <TableCell className={classes.numeric}>{row.quantity ?? '—'}</TableCell>
                                                    {extraFeatures && (
                                                        <TableCell>
                                                            {item ? <StatusChip size="sm" tone={item.tone}>{item.label}</StatusChip> : '—'}
                                                        </TableCell>
                                                    )}
                                                    {canManageOrders && <TableCell className={classes.numeric}>{formatAmount(row.itemPrice)}</TableCell>}
                                                    {canManageOrders && (
                                                        <TableCell className={classes.numeric}>
                                                            {struck ? (
                                                                <span className={classes.struck} title="Canceled with a fee">{formatAmount(row.itemTotalPrice)}</span>
                                                            ) : formatAmount(row.itemTotalPrice)}
                                                        </TableCell>
                                                    )}
                                                    {extraFeatures && canManageOrders && (
                                                        <TableCell>
                                                            <div className={classes.actions}>
                                                                <Button variant="outlined" color="error" size="small" onClick={() => openItemAction(row.orderItemId, 'cancel')} disabled={row.itemStatusId !== 1}>
                                                                    Cancel
                                                                </Button>
                                                                <Button variant="outlined" size="small" onClick={() => openItemAction(row.orderItemId, 'decline')} disabled={row.itemStatusId !== 1}>
                                                                    Decline
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div style={{ padding: 20 }} className={classes.secondary}>This order has no items.</div>
                        )}
                    </Panel>

                    {data.shipments && data.shipments.length > 0 && (
                        <Panel title="Shipments" badges={<StatusChip size="sm" tone="neutral">{data.shipments.length}</StatusChip>} padding="none">
                            <div className={classes.scroll}>
                                <Table aria-label="Shipments">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Shipment</TableCell>
                                            <TableCell>Tracking number</TableCell>
                                            <TableCell>Carrier</TableCell>
                                            <TableCell>Service</TableCell>
                                            <TableCell>Ship date</TableCell>
                                            <TableCell align="right">Cost</TableCell>
                                            {canManageOrders && <TableCell align="right">Label</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.shipments.map((row) => (
                                            <TableRow key={row.shipmentId}>
                                                <TableCell className={classes.mono}>{row.shipmentId}</TableCell>
                                                <TableCell className={classes.mono}>{row.trackingNumber || '—'}</TableCell>
                                                <TableCell>{row.carrierCode || '—'}</TableCell>
                                                <TableCell>{row.serviceCode || '—'}</TableCell>
                                                <TableCell>{formatDate(row.shipDate)}</TableCell>
                                                <TableCell className={classes.numeric}>{formatAmount(row.shipmentCost)}</TableCell>
                                                {canManageOrders && (
                                                    <TableCell align="right">
                                                        {row.voided === 0 ? (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                size="small"
                                                                disabled={!(data.orderStatusId === 7 || data.orderStatusId === 3)}
                                                                onClick={() => voidLabelPopUpHandler({ shipmentId: row.shipmentId })}
                                                            >
                                                                Void label
                                                            </Button>
                                                        ) : (
                                                            <StatusChip size="sm" tone="neutral">Voided</StatusChip>
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Panel>
                    )}

                    {data.events && data.events.length > 0 && (
                        <Panel title="Event history" badges={<StatusChip size="sm" tone="neutral">{data.events.length}</StatusChip>} padding="none">
                            <ol className={classes.timeline}>
                                {data.events.map((event: any, index: number) => (
                                    <li key={index} className={classes.event}>
                                        <span className={classes.eventTime}>{formatDate(event.time, { time: true })}</span>
                                        <span className={classes.eventAction}>{event.action}</span>
                                        <span className={classes.eventDetails}>
                                            {event.source || 'Unknown source'}
                                            {event.userName ? ` · ${event.userName}` : ''}
                                            {event.affectedItems?.length ? ` · items ${event.affectedItems.join(', ')}` : ''}
                                            {event.note ? ` · ${event.note}` : ''}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </Panel>
                    )}
                </div>

                <aside className={classes.side} aria-label="Order summary">
                    {/* External id and dates are already in the page header; the summary keeps only what is not there. */}
                    <Panel
                        title="Summary"
                        padding="sm"
                        bodyClassName={classes.summaryBody}
                        footer={canManageOrders ? (
                            <div className={classes.totals}>
                                <div className={classes.totalRow}>
                                    <span>Order total</span>
                                    <span className={classes.totalValue}>{formatAmount(data.totalOrderPrice)}</span>
                                </div>
                                <div className={`${classes.totalRow} ${classes.grandTotal}`}>
                                    <span>With shipping</span>
                                    <span className={classes.totalValue}>{formatAmount(data.totalOrderPriceWithShipment)}</span>
                                </div>
                            </div>
                        ) : undefined}
                    >
                        <KeyValueList
                            minColumnWidth={110}
                            items={[
                                { label: 'Status', value: <StatusChip tone={status.tone}>{status.label}</StatusChip> },
                                { label: 'Shipping method', value: data.shippingMethodCode },
                                { label: 'In ShipStation', value: data.shipstationCreated ? 'Yes' : 'No' },
                                { label: 'On hold', value: data.onHold ? 'Yes' : 'No' },
                            ]}
                        />
                    </Panel>
                    {canManageOrders && recipient && (
                        <Panel title="Recipient">
                            <address className={classes.address}>
                                <b>{recipient.name || '—'}</b><br />
                                {recipient.street1 && <>{recipient.street1}<br /></>}
                                {recipient.street2 && <>{recipient.street2}<br /></>}
                                {[recipient.city, recipient.state, recipient.postalCode].filter(Boolean).join(', ')}
                                {(recipient.city || recipient.state || recipient.postalCode) && <br />}
                                {recipient.country || ''}
                            </address>
                        </Panel>
                    )}
                </aside>
            </div>
        </>
    );
}
